import { Components, Theme, ThemeOptions } from '@mui/material/styles';
import { SizeMap } from '@/themes';

const getChipTheme = (theme: Theme): ThemeOptions => {
  // Chip Size Map
  // Can be overridden by defining theme.size.chip in muiTheme.ts
  // Example: size: { chip: { s: { height: '28px', padding: '0 8px' }, ... } }
  const chipSize: SizeMap = {
    s: { height: theme.unit(7), padding: theme.unit(0, 2) },
    m: { height: theme.unit(8), padding: theme.unit(0, 3) },
    l: { height: theme.unit(9), padding: theme.unit(0, 4) },
    xl: { height: theme.unit(10), padding: theme.unit(0, 5) },
  };

  const chipTheme: Components<Theme>['MuiChip'] = {
    defaultProps: {
      variant: 'outlined',
      color: 'primary',
      size: 's',
      clickable: false,
    },
    styleOverrides: {
      root: {
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: theme.unit(2),
        gap: theme.unit(2),
        ...theme.typography.M3.label.mediumProminent,
        '& .MuiChip-label': {
          paddingInline: 0,
          ...theme.typography.M3.label.mediumProminent,
        },
        '& .MuiChip-icon, & .MuiChip-deleteIcon': {
          color: 'inherit',
        },
      },
    },
    variants: [
      // ─────────────────────────────
      // SIZE VARIANTS
      // ─────────────────────────────
      {
        props: { size: 's' },
        style: {
          ...chipSize.s, // Spread the size config
        },
      },
      {
        props: { size: 'm' },
        style: {
          ...chipSize.m, // Spread the size config
        },
      },
      {
        props: { size: 'l' },
        style: {
          ...chipSize.l, // Spread the size config
        },
      },
      {
        props: { size: 'xl' },
        style: {
          ...chipSize.xl, // Spread the size config
        },
      },

      // ─────────────────────────────
      // FILLED VARIANTS - NON-CLICKABLE
      // ─────────────────────────────
      {
        props: { variant: 'filled', color: 'primary', clickable: false },
        style: {
          backgroundColor: theme.palette.M3.sys.light.primary,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'default',
        },
      },
      {
        props: { variant: 'filled', color: 'success', clickable: false },
        style: {
          backgroundColor: theme.palette.State.success,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'default',
        },
      },
      {
        props: { variant: 'filled', color: 'warning', clickable: false },
        style: {
          backgroundColor: theme.palette.State.error,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'default',
        },
      },

      // ─────────────────────────────
      // FILLED VARIANTS - CLICKABLE
      // ─────────────────────────────
      {
        props: { variant: 'filled', color: 'primary', clickable: true },
        style: {
          backgroundColor: theme.palette.M3.sys.light.primary,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['primary-fixed'],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'success', clickable: true },
        style: {
          backgroundColor: theme.palette.State.success,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.State['success-pressed'],
          },
        },
      },
      {
        props: { variant: 'filled', color: 'warning', clickable: true },
        style: {
          backgroundColor: theme.palette.State.error,
          color: theme.palette.M3.sys.light['on-primary'],
          border: '1px solid transparent',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.State['error-pressed'],
          },
        },
      },

      // ─────────────────────────────
      // OUTLINED VARIANTS - NON-CLICKABLE
      // ─────────────────────────────
      {
        props: { variant: 'outlined', color: 'primary', clickable: false },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.M3.sys.light['on-surface-variant'],
          border: `1px solid ${theme.palette.M3.sys.light['on-surface-variant']}`,
          cursor: 'default',
        },
      },
      {
        props: { variant: 'outlined', color: 'success', clickable: false },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.State.success,
          border: `1px solid ${theme.palette.M3.sys.light['on-surface-variant']}`,
          cursor: 'default',
        },
      },
      {
        props: { variant: 'outlined', color: 'warning', clickable: false },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.State.error,
          border: `1px solid ${theme.palette.M3.sys.light['on-surface-variant']}`,
          cursor: 'default',
        },
      },

      // ─────────────────────────────
      // OUTLINED VARIANTS - CLICKABLE
      // ─────────────────────────────
      {
        props: { variant: 'outlined', color: 'primary', clickable: true },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.M3.sys.light['on-surface-variant'],
          border: `1px solid ${theme.palette.M3.sys.light['on-surface-variant']}`,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['secondary-container'],
            color: theme.palette.M3.sys.light['on-secondary-container'],
            borderColor: theme.palette.M3.sys.light['secondary-container'],
            '& .MuiChip-icon, & .MuiChip-deleteIcon': {
              color: theme.palette.M3.sys.light['on-secondary-container'],
            },
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'success', clickable: true },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.State.success,
          border: `1px solid ${theme.palette.State.success}`,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['secondary-container'],
            color: theme.palette.M3.sys.light['on-secondary-container'],
            borderColor: theme.palette.M3.sys.light['secondary-container'],
            '& .MuiChip-icon, & .MuiChip-deleteIcon': {
              color: theme.palette.M3.sys.light['on-secondary-container'],
            },
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'warning', clickable: true },
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.State.error,
          border: `1px solid ${theme.palette.State.error}`,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['secondary-container'],
            color: theme.palette.M3.sys.light['on-secondary-container'],
            borderColor: theme.palette.M3.sys.light['secondary-container'],
            '& .MuiChip-icon, & .MuiChip-deleteIcon': {
              color: theme.palette.M3.sys.light['on-secondary-container'],
            },
          },
        },
      },
    ],
  };

  return {
    components: { MuiChip: chipTheme },
    size: { chip: chipSize },
  };
};

export default getChipTheme;
